import { useMemo } from 'react'

import currency from 'currency.js'
import { Text, Card, Separator, CardColors } from '@/ui'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import { LineItem } from './LineItem'
import { PNLSection } from './PNLSection'
import { Expense } from '@/src/actions/reportActions'
import { Loader } from 'semantic-ui-react'
import { TextColors, TypographyStyles } from '@/styles'

export const ProfitAndLossReports = ({
  startDate,
  endDate,
  profitsObj,
  expensesObj,
  uncategorizedObj,
  otherObj,
  fetchingData,
  userIdForAdmin,
  confirmed,
}: {
  startDate: string | undefined
  endDate: string | undefined
  profitsObj:
    | {
        profits: Array<Pick<Expense, 'transactionCategoryId' | 'sum'>>
        sum: number | string
      }
    | null
    | undefined
  expensesObj:
    | {
        expenses: Array<Pick<Expense, 'transactionCategoryId' | 'sum'>>
        sum: number | string
      }
    | null
    | undefined
  uncategorizedObj:
    | {
        sum: number | string
        transactionCategoryId: number | null
      }
    | null
    | undefined
  otherObj:
    | {
        otherExpenses: Array<Pick<Expense, 'transactionCategoryId' | 'sum'>>
        sum: number | string
      }
    | null
    | undefined
  fetchingData: boolean
  userIdForAdmin: number | null
  confirmed?: boolean
}) => {
  const uncategorizedData = useMemo(() => {
    if (!uncategorizedObj?.sum) return null
    const { sum, transactionCategoryId } = uncategorizedObj
    return (
      <div className="flex flex-col gap-4">
        <Separator />
        <Text as={TypographyStyles.H2}>Awaiting Categorization</Text>
        <Text as={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
          Don’t worry about these—your bookkeeper will categorize these
          transactions each month.
        </Text>
        <LineItem
          item={{ sum, transactionCategoryId }}
          startDate={startDate}
          endDate={endDate}
          userIdForAdmin={userIdForAdmin}
        />
      </div>
    )
  }, [uncategorizedObj, startDate, endDate, userIdForAdmin])

  const netIncome = currency(profitsObj?.sum || 0).add(
    expensesObj?.sum || 0
  ).value

  if (fetchingData) return <Loader inline active />

  return (
    <div className="flex flex-col gap-4">
      <PNLSection
        title="Income"
        totalText="Total Income"
        sum={profitsObj?.sum || 0}
        expenses={profitsObj?.profits || []}
        startDate={startDate}
        endDate={endDate}
        userIdForAdmin={userIdForAdmin}
        isProfit
        confirmed={confirmed}
      />
      <PNLSection
        title="Expenses"
        totalText="Total Expenses"
        sum={expensesObj?.sum || 0}
        expenses={expensesObj?.expenses || []}
        startDate={startDate}
        endDate={endDate}
        userIdForAdmin={userIdForAdmin}
        confirmed={confirmed}
      />
      <Card color={CardColors.EMPHASIS}>
        <div className="flex justify-between">
          <Text as={TypographyStyles.BODY_LG}>Net Profit:</Text>
          <Text as={TypographyStyles.BODY_LG}>
            <b>
              <CurrencyFormatLabel value={netIncome} />
            </b>
          </Text>
        </div>
      </Card>
      {uncategorizedData}
      <Separator />
      <PNLSection
        title="Other"
        sum={otherObj?.sum || 0}
        expenses={otherObj?.otherExpenses || []}
        startDate={startDate}
        endDate={endDate}
        userIdForAdmin={userIdForAdmin}
        confirmed={confirmed}
      />
    </div>
  )
}
